import type {
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import type { Renter } from '@/utils/types/renter.interface';

import { Controller } from 'react-hook-form';
import dayjs from 'dayjs';

import { LeaseDatePicker, useLinkedLeaseDateRange } from '@/features/LeaseDetails';

type LeaseEndDatePickerProps = {
  control: Control<Renter, any>;
  errors: FieldErrors<Renter>;
  setValue: UseFormSetValue<Renter>;
  trigger: UseFormTrigger<Renter>;
  watch: UseFormWatch<Renter>;
  endDateManuallyEdited: React.MutableRefObject<boolean>;
};

export const LeaseEndDatePicker = ({
  control,
  errors,
  setValue,
  trigger,
  watch,
  endDateManuallyEdited,
}: LeaseEndDatePickerProps) => {
  const error = errors.leaseEnd;
  const leaseStart = watch('leaseStart');

  const { onEndDateChange, onEndDateError } = useLinkedLeaseDateRange({
    setStartDate: (date, options) =>
      setValue('leaseStart', date ? (date.toDate() as Date) : (null as unknown as Date), options),
    setEndDate: (date, options) =>
      setValue('leaseEnd', date ? (date.toDate() as Date) : (null as unknown as Date), options),
    triggerStartDate: () => trigger('leaseStart'),
    triggerEndDate: () => trigger('leaseEnd'),
    endDateManuallyEdited,
  });

  return (
    <Controller
      name="leaseEnd"
      control={control}
      render={({ field }) => {
        const pickerValue = field.value ? dayjs(field.value) : null;

        return (
          <LeaseDatePicker
            title="Lease end date"
            value={pickerValue}
            onChange={onEndDateChange}
            onError={onEndDateError}
            error={error}
            required
            dataTestId="lease_end"
            minDate={leaseStart ? dayjs(leaseStart).add(1, 'day') : undefined}
            maxDate={leaseStart ? dayjs(leaseStart).add(10, 'year').add(1, 'month') : undefined}
            size="small"
            redAsterisk
          />
        );
      }}
    />
  );
};
